import React, { useState, useEffect, useRef, useContext } from "react";
import {
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import get from "lodash/get";
import uniq from "lodash/uniq";

import { fetchPriceChartData } from "../../action/MarktdatenActions";
import {
  selectMarktdatenName,
  selectGraphData,
  selectGraphFilter,
} from "../../reducer/MarktdatenSelector";
import {
  getQueryChartData,
  getChartData,
  numberWithCommas,
  getChartDomain,
  getUnit,
  getLegendName,
} from "./utils";

import styles from "./MarktdatenInfoSection.module.scss";
import DomainContext from "../../context/DomainContext";
import { withNamespaces } from "react-i18next";

const CustomTooltip = ({ active, payload }) => {
  const domain = useContext(DomainContext);

  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        {payload.reverse().map(p => {
          const value = numberWithCommas(domain)(get(p, "value", 0));
          const unit = get(p, "unit", "");

          return (
            <div
              className={styles.tooltipContainer}
              style={{ borderColor: p.color }}
            >
              <div className={styles.name}>{p.name}</div>
              <div>{get(p, "payload.date")}</div>
              <div>
                {value} {unit}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

const PriceChart = props => {
  /**
   * PROPS
   */
  const { material, tableName, t } = props;
  /**
   * STORE
   */
  const dispatch = useDispatch();
  const graphData = useSelector(selectGraphData);
  const name = useSelector(selectMarktdatenName);
  const { type: period, value: filterValue } = useSelector(selectGraphFilter);
  const [chartData, setChartData] = useState([]);
  /**
   * STATE
   */
  const [graphDimensions, setGraphDimensions] = useState({});
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const ref = useRef(null);

  const handleResize = () => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  /**
   * EFFECT
   */
  useEffect(() => {
    window.addEventListener("resize", handleResize, false);

    return () => {
      window.removeEventListener("resize", handleResize, true);
    };
  }, []);

  // FETCH DATA
  useEffect(() => {
    dispatch(
      fetchPriceChartData(
        getQueryChartData({
          period,
          material,
          filterValue,
          tableName,
        })
      )
    );
  }, [dispatch, material, period, filterValue, tableName]);

  useEffect(() => {
    if (ref.current) {
      const containerWidth =
        ref.current.offsetWidth >= 768 ? ref.current.offsetWidth : 720;
      const height = containerWidth / 2;

      setGraphDimensions({
        width: containerWidth,
        height,
      });
    }
  }, [ref, setGraphDimensions, dimensions]);
  useEffect(() => {
    setChartData(getChartData({ data: graphData, period, tableName, domain }));
  }, [graphData, tableName]);

  const domain = useContext(DomainContext);

  const chartDomain = getChartDomain({ tableName });
  const ticks = uniq(chartData.map(e => e.name));
  const [firstUnit, secondUnit] = getUnit({ tableName });
  const [firstLegend, secondLegend] = getLegendName({ t, tableName, name });
  const firstArrayArea = (
    <Area
      yAxisId="firstArray"
      type="linear"
      dataKey="firstArray"
      name={firstLegend}
      stroke="#000"
      fillOpacity={1}
      fill="url(#colorSettlement)"
      unit={firstUnit}
    />
  );
  const secondArrayArea = (
    <Area
      yAxisId="secondArray"
      type="linear"
      dataKey="secondArray"
      name={secondLegend}
      stroke="#F2B332"
      fillOpacity={1}
      fill="url(#colorStock)"
      unit={secondUnit}
    />
  );

  return (
    <div className={styles.chart} ref={ref}>
      <AreaChart
        width={graphDimensions.width}
        height={graphDimensions.height}
        data={chartData}
        className={styles.chart2}
      >
        <XAxis dataKey="name" ticks={ticks} />
        <YAxis
          yAxisId="firstArray"
          dataKey="firstArray"
          domain={chartDomain}
          tickFormatter={numberWithCommas(domain)}
          width={100}
        />
        {!!secondLegend && (
          <YAxis
            yAxisId="secondArray"
            dataKey="secondArray"
            domain={chartDomain}
            tickFormatter={numberWithCommas(domain)}
            width={100}
            orientation="right"
          />
        )}
        {firstArrayArea}
        {!!secondLegend && secondArrayArea}
      </AreaChart>
      <div className={styles.cover}>
        <AreaChart
          width={graphDimensions.width}
          height={graphDimensions.height}
          data={chartData}
          className={styles.chart1}
        >
          <defs>
            <linearGradient id="colorSettlement" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#B9B8C4" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#B9B8C4" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorStock" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F2B332" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#F2B332" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" ticks={ticks} />
          <YAxis
            yAxisId="firstArray"
            dataKey="firstArray"
            domain={chartDomain}
            tickFormatter={numberWithCommas(domain)}
            width={100}
          />
          {!!secondLegend && (
            <YAxis
              yAxisId="secondArray"
              dataKey="secondArray"
              domain={chartDomain}
              tickFormatter={numberWithCommas(domain)}
              width={100}
              orientation="right"
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          {firstArrayArea}
          {!!secondLegend && secondArrayArea}
        </AreaChart>
      </div>
    </div>
  );
};

PriceChart.propTypes = {
  material: PropTypes.string.isRequired,
  tableName: PropTypes.string.isRequired,
};

export default React.memo(withNamespaces()(PriceChart));
